"use client"

import {
  type ReactNode,
  type Dispatch,
  type SetStateAction,
  type KeyboardEvent,
  Activity,
  useCallback,
  useEffect,
  useId,
  useRef,
} from "react"
import { useParams } from "next/navigation"
import { getT } from "@local/i18n/src/t"
import { getLanguageForMarket } from "@local/i18n/src/languages"
import { a11y } from "@local/i18n/src/catalogs/a11y"
import { CloseIcon } from "./icons"
import { cn } from "@local/utils/src/cn"
import { setBodyOverflow } from "@local/utils/src/setBodyOverflow"

interface Props {
  title?: string
  /**
   * Accessible name for the dialog when it has no visible `title`. Without it
   * the dialog announces as the generic "Dialog". Ignored when `title` is set —
   * a visible label always wins (WCAG 2.5.3), and a dialog must never carry
   * both `aria-labelledby` and `aria-label`.
   */
  ariaLabel?: string
  children?: ReactNode
  open?: boolean
  toggle: Dispatch<SetStateAction<boolean>> | ((e: boolean) => void)
  onCloseCallback?: () => void
  contentClassName?: string
  shorter?: boolean
  disableCloseOnOnverlay?: boolean
  hideCloseButton?: boolean
  skipBodyOverflow?: boolean
}

const Modal = ({
  title,
  ariaLabel,
  open,
  toggle,
  children,
  onCloseCallback,
  contentClassName,
  disableCloseOnOnverlay,
  hideCloseButton,
  skipBodyOverflow,
}: Props) => {
  const modalId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const savedScrollY = useRef(0)
  // Language from the `[market]` route segment (client-side); English elsewhere.
  const params = useParams<{ market?: string }>()
  const t = getT(getLanguageForMarket(params?.market), { a11y })

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        toggle(false)
        onCloseCallback?.()
        return
      }

      if (e.key !== "Tab" || !dialogRef.current) return

      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    },
    [toggle, onCloseCallback]
  )

  useEffect(() => {
    if (skipBodyOverflow) return

    if (open) {
      savedScrollY.current = window.scrollY
      setBodyOverflow(true, { id: modalId })
      document.body.style.top = `-${savedScrollY.current}px`
      document.body.style.left = "0"
      document.body.style.right = "0"
    }

    return () => {
      const scrollY = savedScrollY.current
      setBodyOverflow(false, { id: modalId })
      if (!document.body.style.position) {
        document.body.style.top = ""
        document.body.style.left = ""
        document.body.style.right = ""
        window.scrollTo(0, scrollY)
      }
    }
  }, [open, modalId, skipBodyOverflow])

  useEffect(() => {
    if (!open || !dialogRef.current) return

    const previouslyFocused = document.activeElement as HTMLElement | null

    // Focus the first focusable element inside the dialog
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstContent = Array.from(focusable).find(
      (el) => !el.classList.contains("fixed") // skip backdrop overlay button
    )
    if (firstContent) {
      firstContent.focus()
    } else {
      dialogRef.current.focus()
    }

    return () => {
      previouslyFocused?.focus()
    }
  }, [open])

  const titleId = `modal-title-${modalId}`
  const hasVisibleTitle = !!title

  return (
    <Activity mode={open ? "visible" : "hidden"}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog role with onKeyDown for Escape is the standard modal a11y pattern */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={hasVisibleTitle ? titleId : undefined}
        aria-label={
          hasVisibleTitle ? undefined : (ariaLabel ?? t("a11y.dialog"))
        }
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="fixed inset-0 z-9999 flex items-start justify-center overflow-y-auto p-4"
      >
        <div
          aria-hidden="true"
          className="fixed inset-0 h-full w-full bg-gray-500/75"
          onClick={() => {
            if (!disableCloseOnOnverlay) {
              toggle(false)
              onCloseCallback?.()
            }
          }}
        />
        <div
          className={cn(
            "relative w-full max-w-2xl transform rounded-md bg-white shadow-xl transition-all",
            contentClassName
          )}
        >
          {!hideCloseButton && (
            <div className="z-10 flex justify-end rounded-t-md bg-white px-6 py-4">
              <button
                type="button"
                aria-label={t("a11y.closeModal")}
                className="rounded-md bg-white text-gray-500 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-gray-700 focus-visible:ring-offset-2 focus-visible:outline-hidden"
                onClick={() => {
                  toggle(false)
                  onCloseCallback?.()
                }}
              >
                <span className="sr-only">{t("a11y.close")}</span>
                <CloseIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}

          <div className="rounded-b-md px-6 pb-6">
            {hasVisibleTitle && (
              <h2
                id={titleId}
                className="text-h5 leading-heading font-display mb-4 font-semibold uppercase rtl:text-right"
              >
                {title}
              </h2>
            )}
            {children}
          </div>
        </div>
      </div>
    </Activity>
  )
}

export { Modal }
